/* eslint-disable jsx-a11y/label-has-associated-control */
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { AudioAnswerList } from '../components/audio-answer-list';
import { useQuestion, useReport } from './hooks/use-context';
import type {
  AnswerData,
  CollectionItemData,
  CollectionItemPropertiesData,
} from './types';

function MapContent() {
  const { answers } = useQuestion();

  const answer = answers?.[0];

  if (!answer) return <></>;

  return (
    <>
      <div
        style={{
          height: '100%',
          borderRadius: '0.5rem',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: '#cbd5e1',
        }}
      >
        <img
          style={{
            width: '100%',
            height: 'auto',
          }}
          src={`${answer.content}`}
          alt="img"
        />
      </div>
      <p
        style={{
          marginTop: '0.5rem',
          fontSize: '0.875rem',
          fontWeight: 600,
          color: '#1f2937',
        }}
      >
        {`Latitude: ${answer.geolocation.latitude}, Longitude: ${answer.geolocation.longitude}`}
      </p>
    </>
  );
}

function CollectionItem(props: { collectionItem: CollectionItemData }) {
  const { collectionItem } = props;

  const renderProperty = (property: CollectionItemPropertiesData) => {
    return (
      <div
        key={property.name}
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignContent: 'center',
          justifyContent: 'flex-start',
          fontSize: '0.75rem',
        }}
      >
        <span style={{ marginRight: '0.5rem' }}>{property.name}</span>
        <span>{property.content}</span>
      </div>
    );
  };

  return (
    <div
      style={{
        fontSize: '0.75rem',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: '#f8fafc',
        padding: '0.5rem',
        fontWeight: 600,
      }}
    >
      {[
        `Nome: ${collectionItem?.name}`,
        `Coleção: ${collectionItem?.collection}`,
        `Código: ${collectionItem?.code}`,
        ...(collectionItem?.properties?.map((p) => `${p.name}: ${p.content}`) ||
          []),
      ]
        .filter(Boolean)
        .join(' | ')}
    </div>
  );
}

function CollectionContent() {
  const { answers } = useQuestion();

  const renderItem = (answer: AnswerData) => {
    const { id, collection_item } = answer;
    return <CollectionItem key={id} collectionItem={collection_item} />;
  };

  return (
    <div
      style={{
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        gap: '0.5rem',
      }}
    >
      {answers.map((answer) => renderItem(answer))}
    </div>
  );
}

function CheckboxContent() {
  const { answers } = useQuestion();

  const renderItem = (item: AnswerData) => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: '0.75rem',
          backgroundColor: item.red_flag ? '#c17c36' : 'transparent',
        }}
        key={item.id}
      >
        <input
          type="checkbox"
          defaultChecked
          style={{
            pointerEvents: 'none',
            height: '1.25rem',
            width: '1.25rem',
            borderRadius: '0.25rem',
            border: '1px solid #d1d5db',
            outline: '2px solid transparent',
          }}
        />
        <label
          style={{
            marginLeft: '0.5rem',
            width: '100%',
            paddingTop: '0.75rem',
            paddingBottom: '0.75rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: item.red_flag ? '#ffffff' : '#111827',
          }}
        >
          {item.option}
        </label>
      </div>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ul
        style={{
          borderRadius: '0.5rem',
          backgroundColor: '#f7fafc',
          fontSize: '0.875rem',
          fontWeight: 500,
          color: '#111827',
        }}
      >
        {answers.map((answer, index) => (
          <li
            style={{
              width: '100%',
              overflow: 'hidden',
              borderRadius: '0.5rem',
              border: '1px solid #e5e7eb',
              borderColor: '#e5e7eb',
              backgroundColor: '#ffffff',
              marginBottom: index === answers.length - 1 ? '0' : '5px',
            }}
          >
            {renderItem(answer)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DefaultContent() {
  const { answers } = useQuestion();

  const answer = answers[0];

  return <p>{answer?.content}</p>;
}

function GalleryContent() {
  const { answers } = useQuestion();
  const { header } = useReport();

  const renderItem = (answer: AnswerData) => {
    return (
      <img
        key={answer.id}
        style={{
          objectFit: 'contain',
          height: 'auto',
          marginBottom: '1rem',
          borderRadius: '10px',
          width: '30%',
          padding: '0.5rem',
          backgroundColor: '#f3f3f3',
        }}
        src={`${answer.content}`}
        alt="img"
      />
    );
  };

  const renderLinkItem = (answer: AnswerData) => {
    return (
      <div
        key={answer.id}
        style={{
          marginBottom: '1rem',
          borderRadius: '10px',
          width: '50%',
          padding: '0.5rem',
          textAlign: 'center',
          backgroundColor: '#f3f3f3',
          fontSize: '11px',
        }}
      >
        Imagem salva. Acesse através do <strong>e-mail de pré-vistoria</strong>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', columnGap: '1rem', flexWrap: 'wrap' }}>
      {_.orderBy(answers, 'position', 'asc').map((answer) =>
        header?.checklist_id === '13e0c955-c28b-4df0-8783-6545644f2812' &&
        header?.webhook_pdf
          ? renderLinkItem(answer)
          : renderItem(answer),
      )}
    </div>
  );
}

function AudioContent() {
  const { t } = useTranslation();
  const { answers, attachments } = useQuestion();

  return (
    <AudioAnswerList
      answers={answers || []}
      attachments={attachments || []}
      fallbackName={t('common.audio')}
    />
  );
}

export function AnswerContent() {
  const question = useQuestion();
  switch (question.type) {
    case 'gallery':
    case 'picture':
    case 'signature':
      return <GalleryContent />;
    case 'geolocation':
      return <MapContent />;
    case 'checkbox':
    case 'checkbox_combo':
    case 'radio':
    case 'radio_combo':
      return <CheckboxContent />;
    case 'collections':
      return <CollectionContent />;
    case 'audio':
      return <AudioContent />;
    default:
      return <DefaultContent />;
  }
}
